import axios from 'axios';
import qs from 'qs';
import isAfter from 'date-fns/is_after';
import {call} from 'actionware';
import jwtDecode from 'jwt-decode';
import camelcaseKeys from 'camelcase-keys';
import {get} from 'lodash';

import {logout} from '../identity';

export const config = {
  baseURL: process.env.REACT_APP_API_URL || '',
  timeout: 0,
  paramsSerializer: function(params) {
    return qs.stringify(params, {allowDots: true});
  },
};

export const instance = axios.create(config);
// mock(instance, config);

const helper = {
  _tokenPromise: null,
  _tokenState: null,
  async get(url, params) {
    const {data} = await instance.get(url, {params});
    return data;
  },
  async post(url, payload) {
    const {data} = await instance.post(url, payload);
    return data;
  },
  async put(url, ...rest) {
    const {data} = await instance.put(url, ...rest);
    return data;
  },
  async patch(url, ...rest) {
    const {data} = await instance.patch(url, ...rest);
    return data;
  },
  async delete(url, ...rest) {
    const {data} = await instance.delete(url, ...rest);
    return data;
  },
  authenticate(params) {
    const url = `authentication?${qs.stringify({...params, _: +new Date()})}`;
    return this._handleNewToken(url);
  },
  refreshToken() {
    if (this._tokenPromise) {
      return this._tokenPromise;
    }
    this._tokenPromise = new Promise(async (resolve, reject) => {
      if (!this._tokenState) {
        this._tokenState = JSON.parse(localStorage.getItem('authState'));
      }

      if (!this._tokenState) {
        reject('No Refresh Token');
      } else {
        const params = {
          refreshToken: this._tokenState.refreshToken,
        };
        const url = `authentication/refresh?${qs.stringify(params)}`;
        try {
          const result = await this._handleNewToken(url);
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          this._tokenPromise = null;
        }
      }
    });
    return this._tokenPromise;
  },
  getSavedToken() {
    this._tokenState = JSON.parse(localStorage.getItem('authState'));
    return this._tokenState;
  },
  async _handleNewToken(url) {
    const {data} = await instance.post(url);
    this._tokenState = data.result;
    this._setApiHeader();
    const header = `Bearer ${this._tokenState.authToken}`;
    instance.defaults.headers.common['Authorization'] = header;
    localStorage.setItem('authState', JSON.stringify(data.result));
    return this._tokenState;
  },
  _setApiHeader() {
    if (this._tokenState) {
      const header = `Bearer ${this._tokenState.authToken}`;
      instance.defaults.headers.common['Authorization'] = header;
    }
  },
  _isTokenExpired() {
    return (
      !this._tokenState ||
      isAfter(new Date(), this._tokenState.authTokenExpiration)
    );
  },
};

export const GLOBAL_ERROR_KEY = '__GLOBAL_ERROR__';

const anonymousPaths = [/authentication/gi, /accounts/gi];
instance.interceptors.request.use(
  async config => {
    let originalRequest = config;

    const isAnonymousPath = anonymousPaths.some(matcher =>
      matcher.test(config.url)
    );
    const tokenIsExpired = helper._isTokenExpired();

    if (tokenIsExpired && !isAnonymousPath) {
      try {
        await helper.refreshToken();
        const header = `Bearer ${helper._tokenState.authToken}`;
        originalRequest.headers.common['Authorization'] = header;
        return originalRequest;
      } catch (error) {
        helper._tokenState = null;
        call(logout);
        return Promise.reject(error);
      }
    }
    return config;
  },
  err => {
    return Promise.reject(err);
  }
);

instance.interceptors.response.use(
  response => response,
  async error => {
    const status = get(error, 'response.status', null);
    if ((status && status === 403) || status === 500) {
      console.error(error);
    }
    if (status && status === 401) {
      // TODO: Look at this
      setTimeout(() => {
        call(logout);
      }, 0);
    }
    return Promise.reject(
      get(error, 'response.data', {
        errors: [
          {propertyName: '', errorMessage: error.message || 'Unknown Error'},
        ],
        hasErrors: true,
        result: null,
      })
    );
  }
);

helper._tokenState = JSON.parse(localStorage.getItem('authState')) || {};
helper._setApiHeader();

export function tryGetPersistedAuth() {
  const persistedAuthState =
    JSON.parse(localStorage.getItem('authState')) || {};
  try {
    return persistedAuthState.authToken
      ? camelcaseKeys(jwtDecode(persistedAuthState.authToken))
      : null;
  } catch (error) {
    return null;
  }
}

export default helper;
